import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from '@studio-freight/lenis';

import useBackgroundAudio from './shared/hooks/useBackgroundAudio';
import useNavigation from './shared/hooks/useNavigation';

import Header from './shared/components/Header';
import Hero from './features/home/components/Hero';
import Destinations from './features/destinations/components/Destinations';
import Experiences from './features/experiences/components/Experiences';
import JourneyPlanner from './features/planner/components/JourneyPlanner';
import RetreatBooking from './features/retreats/components/RetreatBooking';
import AIGuide from './features/ai_guide/components/AIGuide';
import Testimonials from './features/stories/components/Testimonials';
import Footer from './shared/components/Footer';
import ScrollToTop from './shared/components/ScrollToTop';
import Login from './features/auth/components/Login';
import Signup from './features/auth/components/Signup';
import Admin from './features/admin/components/Admin';
import Chatbot from './shared/components/Chatbot';
import WhatsAppIcon from './shared/components/WhatsAppIcon';
import PrivacyPolicy from './features/legal/components/PrivacyPolicy';
import TermsOfService from './features/legal/components/TermsOfService';
import TalkToExpert from './features/consult/components/TalkToExpert';
import NewsletterPopup from './shared/components/NewsletterPopup';
import UserDashboard from './features/dashboard/components/UserDashboard';

gsap.registerPlugin(ScrollTrigger);

export default function App() {
  const [customItinerary, setCustomItinerary] = useState(null);
  const { audioPlaying, toggleAudio } = useBackgroundAudio();
  const { activeSection, navigateToSection, hideHeaderFooter, location } = useNavigation();

  const navigate = useNavigate();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true, smoothTouch: false });
    window.lenis = lenis;
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
    return () => { lenis.destroy(); window.lenis = null; };
  }, []);

  useEffect(() => {
    gsap.fromTo('.page-container',
      { opacity: 0, y: 80, scale: 0.98 },
      { opacity: 1, y: 0, scale: 1, duration: 1.4, ease: 'power3.out', stagger: 0.15, clearProps: 'all' }
    );
  }, [location.pathname]);

  const handleBookCustomJourney = (itinerary) => {
    setCustomItinerary(itinerary);
    navigate('/retreats');
  };

  const handleResetCustomItinerary = () => setCustomItinerary(null);

  return (
    <div className="App" style={{ minHeight: '100vh', backgroundColor: '#050505', overflowX: 'hidden' }}>
      <ScrollToTop />

      {!hideHeaderFooter && (
        <Header
          activeSection={activeSection}
          onNavigate={navigateToSection}
          audioPlaying={audioPlaying}
          onToggleAudio={toggleAudio}
        />
      )}

      <Routes>
        <Route path="/" element={<div className="page-container"><Hero onExploreClick={() => navigate('/sanctuary')} onSeekRetreatClick={() => navigate('/retreats')} audioPlaying={audioPlaying} onToggleAudio={toggleAudio} /></div>} />
        <Route path="/sanctuary" element={<div className="page-container"><Destinations /></div>} />
        <Route path="/experiences" element={<div className="page-container"><Experiences /></div>} />
        <Route path="/planner" element={<div className="page-container"><JourneyPlanner onBookCustomJourney={handleBookCustomJourney} /></div>} />
        <Route path="/retreats" element={<div className="page-container"><RetreatBooking customItinerary={customItinerary} onResetCustomItinerary={handleResetCustomItinerary} /></div>} />
        <Route path="/guide" element={<div className="page-container"><AIGuide /></div>} />
        <Route path="/stories" element={<div className="page-container"><Testimonials /></div>} />
        <Route path="/login" element={<div className="page-container"><Login /></div>} />
        <Route path="/signup" element={<div className="page-container"><Signup /></div>} />
        <Route path="/admin" element={<div className="page-container"><Admin /></div>} />
        <Route path="/dashboard" element={<div className="page-container"><UserDashboard /></div>} />
        <Route path="/privacy" element={<div className="page-container"><PrivacyPolicy /></div>} />
        <Route path="/terms" element={<div className="page-container"><TermsOfService /></div>} />
      </Routes>

      {!hideHeaderFooter && <Footer onNavigate={navigateToSection} />}

      {!hideHeaderFooter && (
        <>
          {location.pathname !== '/guide' && <TalkToExpert />}
          <WhatsAppIcon />
          <Chatbot />
          <NewsletterPopup />
        </>
      )}

      <style>{`
        html { scroll-behavior: auto; }
        body { margin: 0; background: #050505; overflow-x: hidden; font-family: 'Inter', sans-serif; }
        * { box-sizing: border-box; }
        .page-container { position: relative; transform-origin: center center; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-thumb { background: rgba(212, 175, 55, 0.5); border-radius: 20px; }
        ::-webkit-scrollbar-track { background: #050505; }
      `}</style>
    </div>
  );
}